import useEmitter from './use_emitter';
import mitt from 'mitt';

function formatURIComponent(v) {
  if (v === true) return '1';
  if (v === false) return '0';
  if (v === null) return '';
  if (typeof v === 'undefined' || typeof v === 'function') return null;
  return encodeURIComponent(v);
}

function formatScalarQuery(k, v) {
  v = formatURIComponent(v);
  if (v === null) return null;
  k = encodeURIComponent(k);
  return `${k}=${v}`;
}

function entries(x) {
  if (x instanceof Map) {
    return [...x.entries()];
  } else {
    return Object.entries(x);
  }
}

function formatArrayQuery(key, ary) {
  key = `${key}[]`;
  return [...ary]
    .map(v => formatScalarQuery(key, v))
    .filter(v => v !== null)
    .join('&');
}

function formatObjectQuery(key, obj) {
  return entries(obj)
    .map(([k, v]) => formatQuery(`${key}[${k}]`, v))
    .filter(v => v !== null)
    .join('&');
}

function isScalar(v) {
  return (typeof v === 'string' || v === null || v === true || v === false || typeof v === 'undefined');
}

function formatQuery(k, v) {
  if (v instanceof Map) {
    return formatObjectQuery(k, v);
  } else if (isScalar(v)) {
    return formatScalarQuery(k, v);
  } else if (v[Symbol.iterator]) {
    return formatArrayQuery(k, v);
  } else if (typeof v === 'object') {
    return formatObjectQuery(k, v);
  } else {
    return formatScalarQuery(k, v);
  }
}

export function toParams(query) {
  return entries(query)
    .map(([k, v]) => formatQuery(k, v))
    .filter(v => v !== null)
    .join('&');
}

function buildHeaders({ json }) {
  let csrf = document.head.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  return {
    'Content-Type': json ? 'application/json' : 'text/plain',
    'X-Requested-With': 'XMLHttpRequest',
    'X-CSRF-Token': csrf,
    'Accept': json ? 'application/json' : '*/*',
  };
}

function doFetch(url, { method = 'GET', body = null, json = true, query = null, signal = null } = {}) {
  let fetchOptions = {
    method,
    headers: buildHeaders({ json }),
    signal
  };

  if (body) {
    fetchOptions.body = json ? JSON.stringify(body) : body;
  }

  if (query) {
    url += '?' + toParams(query);
  }

  if (!url.startsWith('/')) url = '/' + url;

  return fetch('/internal' + url, fetchOptions).catch(() => {});
}

function parseResponse(response) {
  let contentType = response?.headers?.get('content-type');
  if (contentType?.match(/application\/json/)) {
    return response?.json();
  } else {
    return response?.text();
  }
}

function getPagination(headers) {
  const totalCount = parseInt(headers.get('Total-Count'), 10);
  const totalPages = parseInt(headers.get('Total-Pages'), 10);
  const pageItems = parseInt(headers.get('Page-Items'), 10);
  const currentPage = parseInt(headers.get('Current-Page'), 10);

  let offset = (currentPage - 1) * pageItems;

  return { totalCount, totalPages, pageItems, currentPage, offset };
}

function isServerError(status) {
  return status >= 500;
}

export default function useAjax(useWithoutSetup = false) {
  let emitter;
  const controller = new AbortController();
  const signal = controller.signal;

  if (useWithoutSetup) {
    emitter = mitt();
  } else {
    emitter = useEmitter();
  }

  async function ajax(...args) {
    const isObject = args.find(item => typeof item === 'object');
    if (isObject) {
      isObject['signal'] = signal;
    } else {
      args.push({signal});
    }

    let response = await doFetch(...args);
    let data = await parseResponse(response);

    if (isServerError(response?.status)) {
      emitter.emit('serverError', response);
      throw new Error(response?.statusText);
    } else if (response?.status === 404) {
      emitter.emit('notFound');
    } else if (response?.status === 401) {
      emitter.emit('unauthorized');
    }

    return {
      ok: response?.ok,
      status: response?.status,
      data,
      headers: response?.headers,
      signal
    };
  }

  async function ajaxPaginated(url, { query = {}, items = [], page = 1 } = {}) {
    let { ok, status, data, headers } = await ajax(url, { query: { ...query, page } });

    if (ok) {
      const { totalCount, totalPages, offset } = getPagination(headers);

      if (page === 1) items.length = totalCount;
      items.splice(offset, data.length, ...data);
      if (page === 1 && totalPages > 1) {
        await fetchRemainingPages(url, { query, items, totalPages });
      }
      return { ok, status: 200, data: items };
    } else {
      return { ok, status, data };
    }
  }

  async function fetchRemainingPages(url, { query, items, totalPages }) {
    let promises = new Array(totalPages - 1);
    for (let i = 2; i <= totalPages; i++) {
      promises[i - 2] = ajaxPaginated(url, { query, items, page: i });
    }
    await Promise.all(promises);
  }

  function ajaxAbort() {
    controller.abort();
  }

  ajax.paginated = ajaxPaginated;
  ajax.abort = ajaxAbort;

  return ajax;
}
